/**
 * Status derivation: component operating level from sensor readings.
 *
 * Port of status-derivation.js (appendix B section 6). The rule table keeps
 * every threshold of the original; of its 41 literal entries, 34 keys are
 * unique and the seven duplicated keys keep the later definition, which is
 * what a JavaScript object literal does (PORT_NOTES.md lists them). Pure: the
 * element tree and the live readings are passed in, nothing is read from
 * globals, and every comparison, clamp and reason string is the original's.
 */
import type { SubsystemStatus } from '../store';
import { readingKey } from '../store';
import { isRfims } from '../../rfims/registry';
import type {
  DataItem,
  Element,
  HistoryPoint,
  Reading,
  StatusColour,
  StatusLevel,
  Subsystem,
} from '../types';

export const STATUS = {
  OPERATIONAL: 'OPERATIONAL',
  WARNING: 'WARNING',
  DEGRADED: 'DEGRADED',
  OFFLINE: 'OFFLINE',
} as const satisfies Record<StatusLevel, StatusLevel>;

export const STATUS_COLOUR: Record<StatusLevel, StatusColour> = {
  OPERATIONAL: 'green',
  WARNING: 'yellow',
  DEGRADED: 'yellow',
  OFFLINE: 'red',
};

/** OPERATIONAL green, WARNING and DEGRADED yellow, OFFLINE red. */
export function statusToColour(level: StatusLevel): StatusColour {
  return STATUS_COLOUR[level];
}

// ----------------------------------------------------------------- rule types

export interface ThresholdBand {
  min?: number;
  max?: number;
}

export interface Thresholds {
  operational?: ThresholdBand;
  warning?: ThresholdBand;
  degraded?: ThresholdBand;
  offline?: ThresholdBand;
}

export type NumericRuleType =
  | 'rpm'
  | 'temperature'
  | 'percentage'
  | 'signal_level'
  | 'pressure'
  | 'measurement'
  | 'speed'
  | 'data_rate'
  | 'ratio'
  | 'angle';

export interface TrendConfig {
  enabled: boolean;
  /** Rate per week below which the trend is degrading. */
  degradationRate?: number;
  /** Rate per week below which the trend is a critical decline. */
  criticalDropRate?: number;
}

export interface NumericSensorRule {
  type: NumericRuleType;
  unit?: string;
  /** Absent on the drive_system position rule; a normalRange on the data item replaces it. */
  thresholds?: Thresholds | null;
  trendAnalysis?: TrendConfig;
  /** Declared by the original and never read. */
  inverted?: boolean;
  inverseCorrelation?: string;
  positionError?: Thresholds;
  twoSided?: boolean;
  highThresholds?: Thresholds;
}

export interface EnumSensorRule {
  type: 'enum' | 'lock_status';
  values: Record<string, StatusLevel>;
  /** Declared by the original and never read. */
  critical?: boolean;
}

export interface KeywordEnumSensorRule {
  type: 'keyword_enum';
  keywords: Record<string, StatusLevel>;
}

export interface BerSensorRule {
  type: 'ber';
  thresholds: Thresholds;
  inverted?: boolean;
}

export interface CounterSensorRule {
  type: 'counter';
  unit?: string;
  /** Per hour, from the two newest history points. */
  rateThresholds: Thresholds;
}

export type SensorRule =
  NumericSensorRule | EnumSensorRule | KeywordEnumSensorRule | BerSensorRule | CounterSensorRule;

export interface ComponentRule {
  /** Data item that carries the derived level, or null when the component has none. */
  statusSensor: string | null;
  primarySensor?: string;
  /** The component's level is the worst of its own sensors and its children. */
  derivesFromChildren?: boolean;
  rules?: Record<string, SensorRule>;
  /** Declared by the original and never read. */
  aggregateFrom?: string[];
  affects?: string[];
  isTopLevel?: boolean;
}

const OP = STATUS.OPERATIONAL;
const WARN = STATUS.WARNING;
const DEG = STATUS.DEGRADED;
const OFF = STATUS.OFFLINE;

/** The 34 unique component rules; the duplicated keys keep the later definition (PORT_NOTES.md). */
export const COMPONENT_RULES: Record<string, ComponentRule> = {
  // Cooling Fan - derives status from RPM
  acu_cooling_fan: {
    statusSensor: 'fan_status',
    primarySensor: 'fan_speed',
    rules: {
      fan_speed: {
        type: 'rpm',
        unit: 'RPM',
        thresholds: {
          // normalRange in JSON: 1800-2500
          operational: { min: 1800, max: 2500 },
          warning: { min: 1500, max: 1800 },
          degraded: { min: 800, max: 1500 },
          offline: { max: 800 },
        },
        trendAnalysis: {
          enabled: true,
          degradationRate: -50, // RPM drop per week indicates wear
          criticalDropRate: -200, // RPM drop per week indicates imminent failure
        },
      },
    },
  },

  // Antenna Control Unit - derives status from temperature and CPU
  antenna_control_unit: {
    statusSensor: 'status',
    derivesFromChildren: true,
    rules: {
      cabinet_temperature: {
        type: 'temperature',
        unit: '°C',
        thresholds: {
          // normalRange in JSON: 15-32
          operational: { min: 15, max: 32 },
          warning: { min: 32, max: 40 },
          degraded: { min: 40, max: 50 },
          offline: { min: 50 },
        },
        inverseCorrelation: 'acu_cooling_fan.fan_speed',
      },
      cpu_usage: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 0, max: 70 },
          warning: { min: 70, max: 85 },
          degraded: { min: 85, max: 95 },
          offline: { min: 95 },
        },
      },
    },
  },

  // Low Noise Amplifier
  low_noise_amplifier: {
    statusSensor: null,
    rules: {
      gain: {
        type: 'signal_level',
        unit: 'dB',
        thresholds: {
          operational: { min: 40, max: 50 },
          warning: { min: 35, max: 40 },
          degraded: { min: 25, max: 35 },
          offline: { max: 25 },
        },
      },
      noise_figure: {
        type: 'signal_level',
        unit: 'dB',
        inverted: true,
        thresholds: {
          operational: { min: 0, max: 1.0 },
          warning: { min: 1.0, max: 1.5 },
          degraded: { min: 1.5, max: 2.5 },
          offline: { min: 2.5 },
        },
      },
    },
  },

  // Drive System - motors and positioning. statusSensor is 'status', not 'overall_status' (position tracking).
  drive_system: {
    statusSensor: 'status',
    derivesFromChildren: true,
    rules: {
      overall_status: {
        type: 'enum',
        values: {
          'On Target': OP,
          'ON TARGET': OP,
          Moving: OP,
          MOVING: OP,
          Slewing: OP,
          SLEWING: OP,
          Tracking: OP,
          TRACKING: OP,
          Stowed: WARN,
          STOWED: WARN,
          'Position Error': DEG,
          'POSITION ERROR': DEG,
          Fault: OFF,
          FAULT: OFF,
          OFFLINE: OFF,
        },
      },
      motors: {
        type: 'enum',
        values: {
          ACTIVE: OP,
          Active: OP,
          IDLE: OP,
          Idle: OP,
          RUNNING: OP,
          Running: OP,
          STOPPED: WARN,
          Stopped: WARN,
          FAULT: OFF,
          Fault: OFF,
          OFFLINE: OFF,
        },
      },
      motor_temperature: {
        type: 'temperature',
        unit: '°C',
        thresholds: {
          operational: { min: 20, max: 60 },
          warning: { min: 60, max: 75 },
          degraded: { min: 75, max: 90 },
          offline: { min: 90 },
        },
      },
      position: {
        type: 'angle',
        unit: '°',
        // Position itself does not indicate health; the error from the commanded position would, but is never read.
        positionError: {
          operational: { max: 0.01 },
          warning: { max: 0.05 },
          degraded: { max: 0.2 },
          offline: { min: 0.2 },
        },
      },
    },
  },

  // Motors subsystem (standalone reference)
  motors: {
    statusSensor: null,
    rules: {
      status: {
        type: 'enum',
        values: {
          OPERATIONAL: OP,
          RUNNING: OP,
          ACTIVE: OP,
          IDLE: OP,
          Idle: OP,
          WARNING: WARN,
          DEGRADED: DEG,
          STOPPED: WARN,
          FAULT: OFF,
          OFFLINE: OFF,
        },
      },
    },
  },

  // Modem
  modem: {
    statusSensor: 'status',
    rules: {
      carrier_lock: {
        type: 'lock_status',
        values: {
          LOCKED: OP,
          ACQUIRING: WARN,
          SEARCHING: WARN,
          UNLOCKED: DEG,
          'NO SIGNAL': OFF,
        },
      },
      symbol_lock: {
        type: 'lock_status',
        values: {
          LOCKED: OP,
          ACQUIRING: WARN,
          SEARCHING: WARN,
          UNLOCKED: DEG,
        },
      },
      eb_no: {
        type: 'signal_level',
        unit: 'dB',
        thresholds: {
          operational: { min: 10, max: 20 },
          warning: { min: 6, max: 10 },
          degraded: { min: 3, max: 6 },
          offline: { max: 3 },
        },
      },
    },
  },

  // Bit Synchronizer
  bit_synchronizer: {
    statusSensor: 'status',
    rules: {
      sync_status: {
        type: 'lock_status',
        values: {
          LOCKED: OP,
          SYNCHRONIZED: OP,
          SYNCED: OP,
          ACQUIRING: WARN,
          SEARCHING: WARN,
          UNLOCKED: DEG,
          'NO SYNC': OFF,
        },
      },
      frame_sync: {
        type: 'lock_status',
        values: {
          LOCKED: OP,
          CHECK: WARN,
          SEARCH: DEG,
          FLYWHEEL: WARN,
          LOSS: OFF,
        },
      },
      slip_count: {
        type: 'counter',
        unit: 'slips',
        rateThresholds: {
          // Slips per hour
          operational: { max: 1 },
          warning: { max: 5 },
          degraded: { max: 20 },
          offline: { min: 20 },
        },
      },
    },
  },

  // Decoder - status derived from BER (normal 1.0e-9 to 1.5e-9); OFFLINE only when BER is effectively 0 (no signal)
  decoder: {
    statusSensor: 'status',
    rules: {
      ber: {
        type: 'ber',
        inverted: true,
        thresholds: {
          operational: { min: 1e-12, max: 2e-9 },
          warning: { min: 1e-12, max: 1e-6 },
          degraded: { min: 1e-12, max: 1e-4 },
          offline: { max: 1e-12 },
        },
      },
      // corrected_errors and uncorrectable_errors are informational only
    },
  },

  // Frequency Translator
  freq_translator: {
    statusSensor: 'status',
    rules: {
      lo_lock: {
        type: 'lock_status',
        critical: true,
        values: {
          LOCKED: OP,
          ACQUIRING: WARN,
          SEARCHING: WARN,
          UNLOCKED: OFF,
        },
      },
      if_output_level: {
        type: 'signal_level',
        unit: 'dBm',
        thresholds: {
          // Typical IF output for frequency translators: -15 to +5 dBm
          operational: { min: -15, max: 5 },
          warning: { min: -20, max: -15 },
          degraded: { min: -25, max: -20 },
          offline: { max: -25 },
        },
      },
    },
  },

  // HVAC (the key is not a real subsystem id)
  hvac_status: {
    statusSensor: null,
    rules: {
      hvac_status: {
        type: 'enum',
        values: {
          COOLING: OP,
          HEATING: OP,
          IDLE: OP,
          ECO: OP,
          WARNING: WARN,
          MAINTENANCE: DEG,
          FAULT: OFF,
          OFFLINE: OFF,
        },
      },
    },
  },

  // Feed Horn (second definition wins: vswr without the inverted flag)
  feed_horn: {
    statusSensor: null,
    rules: {
      efficiency: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 75, max: 100 },
          warning: { min: 65, max: 75 },
          degraded: { min: 50, max: 65 },
          offline: { max: 50 },
        },
      },
      vswr: {
        type: 'ratio',
        thresholds: {
          operational: { min: 1.0, max: 1.5 },
          warning: { min: 1.5, max: 2.0 },
          degraded: { min: 2.0, max: 3.0 },
          offline: { min: 3.0 },
        },
      },
    },
  },

  // Waveguide (pressurization around 4.0-4.3 PSI in the JSON)
  waveguide: {
    statusSensor: null,
    rules: {
      loss: {
        type: 'signal_level',
        unit: 'dB',
        inverted: true,
        thresholds: {
          operational: { min: 0, max: 0.5 },
          warning: { min: 0.5, max: 1.0 },
          degraded: { min: 1.0, max: 2.0 },
          offline: { min: 2.0 },
        },
      },
      pressurization: {
        type: 'pressure',
        unit: 'PSI',
        thresholds: {
          operational: { min: 3.0, max: 5.0 },
          warning: { min: 2.0, max: 3.0 },
          degraded: { min: 1.0, max: 2.0 },
          offline: { max: 1.0 },
        },
      },
    },
  },

  // Primary Reflector (second definition wins: surface_accuracy 0-3 mm with a min bound)
  primary_reflector: {
    statusSensor: null,
    rules: {
      efficiency: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 55, max: 100 },
          warning: { min: 45, max: 55 },
          degraded: { min: 35, max: 45 },
          offline: { max: 35 },
        },
      },
      surface_accuracy: {
        type: 'measurement',
        unit: 'mm',
        thresholds: {
          operational: { min: 0, max: 3.0 },
          warning: { min: 3.0, max: 4.0 },
          degraded: { min: 4.0, max: 5.0 },
          offline: { min: 5.0 },
        },
      },
    },
  },

  // Pedestal (second definition wins: settlement 0-5 mm)
  pedestal: {
    statusSensor: 'status',
    rules: {
      foundation_settlement: {
        type: 'measurement',
        unit: 'mm',
        thresholds: {
          operational: { min: 0, max: 5 },
          warning: { min: 5, max: 10 },
          degraded: { min: 10, max: 20 },
          offline: { min: 20 },
        },
      },
    },
  },

  // Beacon Receiver (second definition wins: mixed-case values, critical flag, affects)
  beacon_receiver: {
    statusSensor: null,
    rules: {
      lock_status: {
        type: 'lock_status',
        critical: true,
        values: {
          LOCKED: OP,
          Locked: OP,
          TRACKING: OP,
          Tracking: OP,
          ACQUIRING: WARN,
          Acquiring: WARN,
          SEARCHING: WARN,
          Searching: WARN,
          UNLOCKED: DEG,
          Unlocked: DEG,
          'NO BEACON': OFF,
          'No Beacon': OFF,
        },
      },
    },
    affects: ['tracking_processor', 'pointing_status'],
  },

  // Tracking Processor (second definition wins: operational up to 80 %)
  tracking_processor: {
    statusSensor: null,
    rules: {
      processing_load: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 0, max: 80 },
          warning: { min: 80, max: 90 },
          degraded: { min: 90, max: 98 },
          offline: { min: 98 },
        },
      },
    },
  },

  // IFL Switch (second definition wins: adds redundancy_mode)
  ifl_switch: {
    statusSensor: 'status',
    rules: {
      fiber_power_primary: {
        type: 'signal_level',
        unit: 'dBm',
        thresholds: {
          operational: { min: -10, max: 0 },
          warning: { min: -15, max: -10 },
          degraded: { min: -20, max: -15 },
          offline: { max: -20 },
        },
      },
      fiber_power_backup: {
        type: 'signal_level',
        unit: 'dBm',
        thresholds: {
          operational: { min: -10, max: 0 },
          warning: { min: -15, max: -10 },
          degraded: { min: -20, max: -15 },
          offline: { max: -20 },
        },
      },
      redundancy_mode: {
        type: 'enum',
        values: {
          ACTIVE: OP,
          Active: OP,
          AUTO: OP,
          Auto: OP,
          PRIMARY: OP,
          Primary: OP,
          BACKUP: WARN,
          Backup: WARN,
          MANUAL: WARN,
          Manual: WARN,
          FAILED: OFF,
          Failed: OFF,
        },
      },
    },
    affects: ['if_distribution', 'modem', 'decoder'],
  },

  // IF Distribution (second definition wins: input -20 to -10 dBm, gain 15-22 dB)
  if_distribution: {
    statusSensor: 'status',
    rules: {
      input_level: {
        type: 'signal_level',
        unit: 'dBm',
        thresholds: {
          operational: { min: -20, max: -10 },
          warning: { min: -25, max: -20 },
          degraded: { min: -30, max: -25 },
          offline: { max: -30 },
        },
      },
      distribution_amp_gain: {
        type: 'signal_level',
        unit: 'dB',
        thresholds: {
          operational: { min: 15, max: 22 },
          warning: { min: 12, max: 15 },
          degraded: { min: 8, max: 12 },
          offline: { max: 8 },
        },
      },
    },
  },

  // Switch Matrix (typical throughput around 860-900 Mbps)
  switch_matrix: {
    statusSensor: 'status',
    rules: {
      throughput: {
        type: 'data_rate',
        unit: 'Mbps',
        thresholds: {
          operational: { min: 800, max: 1200 },
          warning: { min: 600, max: 800 },
          degraded: { min: 300, max: 600 },
          offline: { max: 300 },
        },
      },
    },
  },

  // Storage Servers
  storage_servers: {
    statusSensor: 'status',
    rules: {
      storage_used: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 0, max: 80 },
          warning: { min: 80, max: 90 },
          degraded: { min: 90, max: 95 },
          offline: { min: 95 },
        },
      },
      raid_status: {
        type: 'enum',
        values: {
          OPTIMAL: OP,
          Optimal: OP,
          REBUILDING: WARN,
          Rebuilding: WARN,
          DEGRADED: DEG,
          Degraded: DEG,
          FAILED: OFF,
          Failed: OFF,
        },
      },
    },
  },

  // Environmental sensors (also the RFIMS cards)
  environmental: {
    statusSensor: null,
    rules: {
      wind_speed: {
        type: 'speed',
        unit: 'm/s',
        thresholds: {
          operational: { min: 0, max: 15 },
          warning: { min: 15, max: 25 },
          degraded: { min: 25, max: 35 },
          offline: { min: 35 },
        },
      },
      temperature: {
        type: 'temperature',
        unit: '°C',
        thresholds: {
          operational: { min: -10, max: 40 },
          warning: { min: -20, max: -10 },
          degraded: { min: -30, max: -20 },
          offline: { max: -30 },
        },
        twoSided: true,
        highThresholds: {
          warning: { min: 40, max: 45 },
          degraded: { min: 45, max: 50 },
          offline: { min: 50 },
        },
      },
      humidity: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 20, max: 80 },
          warning: { min: 80, max: 90 },
          degraded: { min: 90, max: 95 },
          offline: { min: 95 },
        },
      },
    },
  },

  // Container/Building
  container_building: {
    statusSensor: 'overall_status',
    rules: {
      room_temperature: {
        type: 'temperature',
        unit: '°C',
        thresholds: {
          operational: { min: 18, max: 28 },
          warning: { min: 28, max: 35 },
          degraded: { min: 35, max: 40 },
          offline: { min: 40 },
        },
      },
    },
  },

  // Backup Structure (large dishes deflect 8-12 mm under normal wind loads)
  backup_structure: {
    statusSensor: 'status',
    rules: {
      deflection: {
        type: 'measurement',
        unit: 'mm',
        thresholds: {
          operational: { min: 0, max: 12 },
          warning: { min: 12, max: 20 },
          degraded: { min: 20, max: 30 },
          offline: { min: 30 },
        },
      },
    },
  },

  // Sub-modems (Raw Data, CDA, GRB)
  raw_data_modem: {
    statusSensor: null,
    rules: {
      lock_status: {
        type: 'lock_status',
        values: { LOCKED: OP, Locked: OP, ACQUIRING: WARN, Acquiring: WARN, UNLOCKED: OFF, Unlocked: OFF },
      },
    },
  },
  cda_modem: {
    statusSensor: null,
    rules: {
      lock_status: {
        type: 'lock_status',
        values: { LOCKED: OP, Locked: OP, ACQUIRING: WARN, Acquiring: WARN, UNLOCKED: OFF, Unlocked: OFF },
      },
    },
  },
  grb_modem: {
    statusSensor: null,
    rules: {
      lock_status: {
        type: 'lock_status',
        values: { LOCKED: OP, Locked: OP, ACQUIRING: WARN, Acquiring: WARN, UNLOCKED: OFF, Unlocked: OFF },
      },
    },
  },

  // Front End Processors (FEPs)
  raw_data_fep: {
    statusSensor: null,
    rules: {
      sync_status: {
        type: 'lock_status',
        values: { SYNCHRONIZED: OP, Synchronized: OP, SYNCED: OP, ACQUIRING: WARN, 'NO SYNC': OFF },
      },
    },
  },
  tc_fep: {
    statusSensor: null,
    rules: {
      sync_status: {
        type: 'lock_status',
        values: { SYNCHRONIZED: OP, Synchronized: OP, SYNCED: OP, ACQUIRING: WARN, 'NO SYNC': OFF },
      },
    },
  },
  grb_fep: {
    statusSensor: null,
    rules: {
      sync_status: {
        type: 'lock_status',
        values: { SYNCHRONIZED: OP, Synchronized: OP, SYNCED: OP, ACQUIRING: WARN, 'NO SYNC': OFF },
      },
    },
  },

  // Pointing Status - derived from overall_status; tracking and pointing_error are informational
  pointing_status: {
    statusSensor: 'overall_status',
    aggregateFrom: ['drive_system', 'beacon_receiver', 'tracking_processor'],
    rules: {
      overall_status: {
        type: 'enum',
        values: {
          Locked: OP,
          LOCKED: OP,
          Tracking: OP,
          TRACKING: OP,
          Slewing: OP,
          SLEWING: OP,
          Acquiring: WARN,
          ACQUIRING: WARN,
          Stowed: WARN,
          STOWED: WARN,
          Error: DEG,
          ERROR: DEG,
          Fault: OFF,
          FAULT: OFF,
          // Derived status values (when status is written back)
          OPERATIONAL: OP,
          Operational: OP,
          WARNING: WARN,
          Warning: WARN,
          DEGRADED: DEG,
          Degraded: DEG,
          OFFLINE: OFF,
          Offline: OFF,
        },
      },
    },
  },

  // RF Performance - signal chain health
  rf_performance: {
    statusSensor: 'overall_status',
    aggregateFrom: ['low_noise_amplifier', 'feed_horn', 'modem', 'freq_translator'],
    rules: {
      overall_status: {
        type: 'enum',
        values: {
          Signal: OP,
          SIGNAL: OP,
          Marginal: WARN,
          MARGINAL: WARN,
          'Weak Signal': DEG,
          'WEAK SIGNAL': DEG,
          'No Signal': OFF,
          'NO SIGNAL': OFF,
          OPERATIONAL: OP,
          Operational: OP,
          WARNING: WARN,
          Warning: WARN,
          DEGRADED: DEG,
          Degraded: DEG,
          OFFLINE: OFF,
          Offline: OFF,
        },
      },
      signal_level: {
        type: 'signal_level',
        unit: 'dBm',
        thresholds: {
          // Based on normalRange in JSON: -130 to -115 dBm
          operational: { min: -130, max: -115 },
          warning: { min: -135, max: -130 },
          degraded: { min: -140, max: -135 },
          offline: { max: -140 },
        },
      },
      link_quality: {
        type: 'percentage',
        unit: '%',
        thresholds: {
          operational: { min: 80, max: 100 },
          warning: { min: 60, max: 80 },
          degraded: { min: 40, max: 60 },
          offline: { max: 40 },
        },
      },
    },
  },

  // System Health - top level aggregation
  system_health: {
    statusSensor: 'overall_status',
    isTopLevel: true,
    aggregateFrom: ['physical_components', 'environmental', 'pointing_status', 'rf_performance'],
    rules: {
      overall_status: {
        type: 'enum',
        values: {
          Green: OP,
          GREEN: OP,
          Yellow: WARN,
          YELLOW: WARN,
          Orange: DEG,
          ORANGE: DEG,
          Red: OFF,
          RED: OFF,
          OPERATIONAL: OP,
          Operational: OP,
          WARNING: WARN,
          Warning: WARN,
          DEGRADED: DEG,
          Degraded: DEG,
          OFFLINE: OFF,
          Offline: OFF,
        },
      },
      power: {
        type: 'enum',
        values: {
          NORMAL: OP,
          Normal: OP,
          UPS: WARN,
          Ups: WARN,
          GENERATOR: WARN,
          Generator: WARN,
          FAILING: DEG,
          Failing: DEG,
          FAILED: OFF,
          Failed: OFF,
          OPERATIONAL: OP,
          WARNING: WARN,
          DEGRADED: DEG,
          OFFLINE: OFF,
        },
      },
      network: {
        type: 'enum',
        values: {
          CONNECTED: OP,
          Connected: OP,
          INTERMITTENT: WARN,
          Intermittent: WARN,
          LIMITED: DEG,
          Limited: DEG,
          DISCONNECTED: OFF,
          Disconnected: OFF,
          OPERATIONAL: OP,
          WARNING: WARN,
          DEGRADED: DEG,
          OFFLINE: OFF,
        },
      },
      alarms: {
        type: 'enum',
        values: {
          NONE: OP,
          None: OP,
          'WIND WARNING': WARN,
          'Wind Warning': WARN,
          MAINTENANCE: WARN,
          Maintenance: WARN,
          'EQUIPMENT FAULT': DEG,
          'Equipment Fault': DEG,
          CRITICAL: OFF,
          Critical: OFF,
          OPERATIONAL: OP,
          WARNING: WARN,
          DEGRADED: DEG,
          OFFLINE: OFF,
        },
      },
    },
  },

  // Physical Components - aggregates all hardware
  physical_components: {
    statusSensor: 'overall_status',
    aggregateFrom: ['main_antenna', 'container_building'],
    derivesFromChildren: true,
  },

  // Main Antenna - aggregates antenna hardware
  main_antenna: {
    statusSensor: null,
    aggregateFrom: [
      'antenna_control_unit',
      'drive_system',
      'low_noise_amplifier',
      'waveguide',
      'feed_horn',
      'primary_reflector',
    ],
    derivesFromChildren: true,
  },
};

/** Key: affected component, value: components whose failure cascades into it. */
export const COMPONENT_DEPENDENCIES: Record<string, string[]> = {
  // Signal chain: LNA, freq_translator, modem, decoder, switch_matrix
  freq_translator: ['low_noise_amplifier'],
  modem: ['freq_translator', 'ifl_switch', 'if_distribution'],
  bit_synchronizer: ['modem'],
  decoder: ['bit_synchronizer'],
  switch_matrix: ['decoder'],
  storage_servers: ['switch_matrix'],

  // Tracking chain: beacon, tracking, drive, pointing
  tracking_processor: ['beacon_receiver'],
  drive_system: ['tracking_processor', 'antenna_control_unit'],
  pointing_status: ['drive_system', 'beacon_receiver'],

  // Cooling: fan, ACU temperature, system
  antenna_control_unit: ['acu_cooling_fan'],

  // RF performance depends on the entire signal chain
  rf_performance: ['low_noise_amplifier', 'feed_horn', 'modem', 'pointing_status'],

  // System health is top-level
  system_health: ['physical_components', 'environmental', 'pointing_status', 'rf_performance'],
};

// ------------------------------------------------------------------ helpers

/** The text before a ratio's colon ("1.15:1" gives "1.15"); other strings unchanged. */
export function numericText(value: string): string {
  const colon = value.indexOf(':');
  return colon === -1 ? value : value.slice(0, colon);
}

/** Numeric part of a reading: a ratio's leading number, commas dropped, then everything but digits, '.', '-' and 'e' (either case). */
export function parseNumericValue(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const cleaned = numericText(value)
      .replace(/,/g, '')
      .replace(/[^0-9.\-e]/gi, '');
    return parseFloat(cleaned);
  }
  return NaN;
}

export function getStatusPriority(status: StatusLevel | string | null | undefined): number {
  switch (status) {
    case STATUS.OPERATIONAL:
      return 0;
    case STATUS.WARNING:
      return 1;
    case STATUS.DEGRADED:
      return 2;
    case STATUS.OFFLINE:
      return 3;
    default:
      return 0;
  }
}

/**
 * Level for a numeric value. Bands are tried operational, warning, degraded;
 * an offline band, when defined, matches any remaining value (the original's
 * `value >= min || value <= max` with an open side); without one, a value
 * more than the operational span outside it is OFFLINE, otherwise DEGRADED.
 * Returns null when the value is NaN or no bands exist.
 */
export function getStatusFromThresholds(
  value: number,
  thresholds: Thresholds | null | undefined,
): StatusLevel | null {
  if (isNaN(value)) return null;
  if (!thresholds || (!thresholds.operational && !thresholds.warning && !thresholds.degraded)) return null;

  if (thresholds.operational) {
    const op = thresholds.operational;
    const opMin = op.min !== undefined ? op.min : -Infinity;
    const opMax = op.max !== undefined ? op.max : Infinity;
    if (value >= opMin && value <= opMax) return STATUS.OPERATIONAL;
  }
  if (thresholds.warning) {
    const warn = thresholds.warning;
    const warnMin = warn.min !== undefined ? warn.min : -Infinity;
    const warnMax = warn.max !== undefined ? warn.max : Infinity;
    if (value >= warnMin && value <= warnMax) return STATUS.WARNING;
  }
  if (thresholds.degraded) {
    const deg = thresholds.degraded;
    const degMin = deg.min !== undefined ? deg.min : -Infinity;
    const degMax = deg.max !== undefined ? deg.max : Infinity;
    if (value >= degMin && value <= degMax) return STATUS.DEGRADED;
  }
  if (thresholds.offline) {
    const off = thresholds.offline;
    // Each specified endpoint is a failure boundary. An absent endpoint
    // must not match the entire number line.
    if ((off.min !== undefined && value >= off.min) || (off.max !== undefined && value <= off.max))
      return STATUS.OFFLINE;
  }
  if (thresholds.operational) {
    const opRange = thresholds.operational;
    const opRangeMin = opRange.min !== undefined ? opRange.min : 0;
    const opRangeMax = opRange.max !== undefined ? opRange.max : 100;
    const range = opRangeMax - opRangeMin;
    if (value < opRangeMin - range || value > opRangeMax + range) return STATUS.OFFLINE;
    return STATUS.DEGRADED;
  }
  return STATUS.OPERATIONAL;
}

/** Upper-cased lookup first, then the raw value; unknown values are OPERATIONAL. */
export function getStatusFromEnum(value: unknown, valueMap: Record<string, StatusLevel>): StatusLevel {
  const upperValue = String(value).toUpperCase();
  return valueMap[upperValue] || valueMap[String(value)] || STATUS.OPERATIONAL;
}

export interface TrendAnalysis {
  trend: 'stable' | 'degrading' | 'critical_decline';
  /** Per week. */
  rate: number;
  concern: boolean;
  dataPoints?: number;
}

/** Rate per week between the oldest and newest numeric points; needs at least 0.1 week of span. */
export function analyzeTrend(
  history: HistoryPoint[] | undefined,
  trendConfig: TrendConfig | undefined,
): TrendAnalysis {
  if (!history || history.length < 2 || !trendConfig || !trendConfig.enabled) {
    return { trend: 'stable', rate: 0, concern: false };
  }
  const dataPoints = history
    .map((h) => ({ value: parseNumericValue(h.value), time: new Date(h.datetime).getTime() }))
    .filter((d) => !isNaN(d.value) && !isNaN(d.time));
  if (dataPoints.length < 2) return { trend: 'stable', rate: 0, concern: false };

  dataPoints.sort((a, b) => a.time - b.time);
  const first = dataPoints[0];
  const last = dataPoints[dataPoints.length - 1];
  const timeDiffWeeks = (last.time - first.time) / (1000 * 60 * 60 * 24 * 7);
  if (timeDiffWeeks < 0.1) return { trend: 'stable', rate: 0, concern: false };

  const ratePerWeek = (last.value - first.value) / timeDiffWeeks;
  let trend: TrendAnalysis['trend'] = 'stable';
  let concern = false;
  if (trendConfig.degradationRate !== undefined) {
    if (ratePerWeek < trendConfig.degradationRate) {
      trend = 'degrading';
      concern = true;
    }
    if (trendConfig.criticalDropRate !== undefined && ratePerWeek < trendConfig.criticalDropRate) {
      trend = 'critical_decline';
      concern = true;
    }
  }
  return { trend, rate: ratePerWeek, concern, dataPoints: dataPoints.length };
}

/** normalRange as shipped ({low, high}) or as the sensor definitions spell it ({min, max}). */
export interface NormalRangeLike {
  low?: number;
  high?: number;
  min?: number;
  max?: number;
  unit?: string;
}

/** Operational = the normal range, warning = ±20 % of its span, degraded = ±40 %; no offline band. */
export function thresholdsFromNormalRange(range: NormalRangeLike): Thresholds | null {
  const normalMin = range.low !== undefined ? range.low : range.min;
  const normalMax = range.high !== undefined ? range.high : range.max;
  if (normalMin === undefined || normalMax === undefined || isNaN(normalMin) || isNaN(normalMax)) return null;
  const margin = (normalMax - normalMin) * 0.2;
  return {
    operational: { min: normalMin, max: normalMax },
    warning: { min: normalMin - margin, max: normalMax + margin },
    degraded: { min: normalMin - margin * 2, max: normalMax + margin * 2 },
  };
}

/** The data item fields the derivation reads. */
export interface SensorInput {
  id: string;
  label?: string;
  value: unknown;
  status?: string;
  history?: HistoryPoint[];
  normalRange?: NormalRangeLike;
}

export interface SensorDerivation {
  status: StatusLevel;
  colour: StatusColour;
  numericValue: number;
  reason: string;
}

/** Level of one data item under one rule; undeterminable values are OPERATIONAL. */
export function deriveSensorStatus(dataItem: SensorInput, sensorRule: SensorRule): SensorDerivation {
  const value = dataItem.value;
  const numericValue = parseNumericValue(value);
  let status: StatusLevel | null = null;
  let reason = '';

  switch (sensorRule.type) {
    case 'rpm':
    case 'temperature':
    case 'percentage':
    case 'signal_level':
    case 'pressure':
    case 'measurement':
    case 'speed':
    case 'data_rate':
    case 'ratio':
    case 'angle': {
      let thresholds = sensorRule.thresholds;
      if (dataItem.normalRange) {
        const built = thresholdsFromNormalRange(dataItem.normalRange);
        if (built) thresholds = built;
      }
      if (
        !dataItem.normalRange &&
        sensorRule.twoSided &&
        sensorRule.highThresholds &&
        numericValue > (thresholds?.operational?.max ?? Infinity)
      ) {
        thresholds = { operational: thresholds?.operational, ...sensorRule.highThresholds };
      }
      status = getStatusFromThresholds(numericValue, thresholds);
      if (status && sensorRule.trendAnalysis && dataItem.history) {
        const trend = analyzeTrend(dataItem.history, sensorRule.trendAnalysis);
        if (trend.concern && status === STATUS.OPERATIONAL) {
          status = STATUS.WARNING;
          reason = 'Trending ' + trend.trend + ' (' + trend.rate.toFixed(1) + '/week)';
        }
      }
      break;
    }
    case 'lock_status':
    case 'enum':
      if (sensorRule.values) status = getStatusFromEnum(value, sensorRule.values);
      break;
    case 'keyword_enum': {
      if (sensorRule.keywords) {
        const valueStr = String(value);
        let foundStatus: StatusLevel | null = null;
        for (const keyword of Object.keys(sensorRule.keywords)) {
          if (valueStr.indexOf(keyword) !== -1) {
            foundStatus = sensorRule.keywords[keyword];
            break;
          }
        }
        status = foundStatus !== null ? foundStatus : STATUS.OPERATIONAL;
      }
      break;
    }
    case 'ber': {
      const berValue = parseFloat(String(value).replace(/[^0-9.\-e]/gi, ''));
      if (!isNaN(berValue) && sensorRule.thresholds) {
        status = getStatusFromThresholds(berValue, sensorRule.thresholds);
      }
      break;
    }
    case 'counter': {
      if (dataItem.history && dataItem.history.length >= 2 && sensorRule.rateThresholds) {
        const recent = dataItem.history.slice(0, 2);
        const val1 = parseNumericValue(recent[0].value);
        const val2 = parseNumericValue(recent[1].value);
        const t1 = new Date(recent[0].datetime).getTime();
        const t2 = new Date(recent[1].datetime).getTime();
        const hoursDiff = (t1 - t2) / (1000 * 60 * 60);
        if (hoursDiff > 0) {
          const rate = (val1 - val2) / hoursDiff;
          status = getStatusFromThresholds(rate, sensorRule.rateThresholds);
        }
      }
      break;
    }
  }

  if (status === null) status = STATUS.OPERATIONAL;
  return { status, colour: STATUS_COLOUR[status], numericValue, reason };
}

/** A subsystem as the derivation sees it (Subsystem is assignable). */
export interface ComponentInput {
  id: string;
  label?: string;
  data?: SensorInput[];
  childs?: ComponentInput[];
  subsystems?: ComponentInput[];
}

export interface SensorStatus {
  sensorId: string;
  sensorName: string;
  status: StatusLevel;
  colour: StatusColour;
  value: unknown;
  reason: string;
}

export interface ComponentDerivation {
  componentId: string;
  componentName: string;
  status: StatusLevel;
  colour: StatusColour;
  /** One entry per data item that had a rule (or a normalRange). Empty means nothing was evaluated. */
  sensorStatuses: SensorStatus[];
  reasons: string[];
}

/**
 * Worst level over the component's ruled sensors (items without a rule but
 * with a normalRange count as generic measurements) and, when the rule says
 * derivesFromChildren, over its children derived with their own rules.
 */
export function deriveComponentStatus(component: ComponentInput, rules?: ComponentRule): ComponentDerivation {
  const componentId = component.id;
  const componentRules: ComponentRule = rules || COMPONENT_RULES[componentId] || { statusSensor: null };
  const sensorRules = componentRules.rules || {};

  const sensorStatuses: SensorStatus[] = [];
  let worstStatus: StatusLevel = STATUS.OPERATIONAL;
  const statusReasons: string[] = [];

  if (component.data && component.data.length > 0) {
    for (const dataItem of component.data) {
      let sensorRule: SensorRule | undefined = sensorRules[dataItem.id];
      if (!sensorRule && dataItem.normalRange) {
        sensorRule = { type: 'measurement', thresholds: null };
      }
      if (sensorRule) {
        const result = deriveSensorStatus(dataItem, sensorRule);
        sensorStatuses.push({
          sensorId: dataItem.id,
          sensorName: dataItem.label || dataItem.id,
          status: result.status,
          colour: result.colour,
          value: dataItem.value,
          reason: result.reason,
        });
        if (getStatusPriority(result.status) > getStatusPriority(worstStatus)) {
          worstStatus = result.status;
          if (result.reason) statusReasons.push(dataItem.label + ': ' + result.reason);
          else statusReasons.push(dataItem.label + ' is ' + result.status);
        }
      }
    }
  }

  if (componentRules.derivesFromChildren) {
    const children = component.childs || component.subsystems || [];
    for (const child of children) {
      const childResult = deriveComponentStatus(child);
      if (getStatusPriority(childResult.status) > getStatusPriority(worstStatus)) {
        worstStatus = childResult.status;
        statusReasons.push(child.label + ' is ' + childResult.status);
      }
    }
  }

  return {
    componentId,
    componentName: component.label || componentId,
    status: worstStatus,
    colour: STATUS_COLOUR[worstStatus],
    sensorStatuses,
    reasons: statusReasons,
  };
}

export interface SystemHealthAggregate {
  /** Green, Yellow, Orange or Red: the worst component level. */
  overallStatus: 'Green' | 'Yellow' | 'Orange' | 'Red';
  overallColour: StatusColour;
  /** Always NORMAL: no rule ever degrades power. */
  power: 'NORMAL' | 'ABNORMAL';
  powerColour: StatusColour;
  network: 'CONNECTED' | 'INTERMITTENT' | 'LIMITED' | 'DISCONNECTED';
  networkColour: StatusColour;
  alarms: 'NONE' | 'WARNING' | 'EQUIPMENT FAULT' | 'CRITICAL';
  alarmsColour: StatusColour;
  alarmDetails: string[];
  componentCount: number;
  degradedCount: number;
  warningCount: number;
}

interface ElementLike {
  subsystems?: ComponentInput[];
  childs?: ComponentInput[];
}

/**
 * Worst level over every ruled component that carries a status or
 * overall_status item (a component with both is counted twice, as the
 * original did), the network from switch_matrix and ifl_switch, power always
 * NORMAL, and the alarm word from the overall level. system_health itself is skipped.
 */
export function aggregateSystemHealth(element: ElementLike): SystemHealthAggregate {
  const componentStatuses: { componentId: string; status: StatusLevel }[] = [];
  const alarmMessages: string[] = [];
  let networkStatus = STATUS.OPERATIONAL as StatusLevel;
  const powerStatus = STATUS.OPERATIONAL as StatusLevel;

  const collect = (items: ComponentInput[] | undefined): void => {
    if (!items) return;
    for (const item of items) {
      if (item.id === 'system_health') continue;
      const hasExplicitRules = Boolean(COMPONENT_RULES[item.id]);
      if (item.data && hasExplicitRules) {
        for (const dataItem of item.data) {
          if (dataItem.id === 'status' || dataItem.id === 'overall_status') {
            const derivation = deriveComponentStatus(item);
            if (derivation.sensorStatuses.length > 0) {
              componentStatuses.push({ componentId: item.id, status: derivation.status });
              if (derivation.status === STATUS.WARNING) alarmMessages.push(item.label + ': WARNING');
              else if (derivation.status === STATUS.DEGRADED) alarmMessages.push(item.label + ': DEGRADED');
              else if (derivation.status === STATUS.OFFLINE) alarmMessages.push(item.label + ': OFFLINE');
            }
          }
          if (item.id === 'switch_matrix' || item.id === 'ifl_switch') {
            const netStatus = deriveComponentStatus(item).status;
            if (getStatusPriority(netStatus) > getStatusPriority(networkStatus)) networkStatus = netStatus;
          }
        }
      }
      collect(item.subsystems);
      collect(item.childs);
    }
  };
  collect(element.subsystems);
  collect(element.childs);

  let overallStatus = STATUS.OPERATIONAL as StatusLevel;
  for (const cs of componentStatuses) {
    if (getStatusPriority(cs.status) > getStatusPriority(overallStatus)) overallStatus = cs.status;
  }
  const overallWord: SystemHealthAggregate['overallStatus'] =
    overallStatus === STATUS.OFFLINE
      ? 'Red'
      : overallStatus === STATUS.DEGRADED
        ? 'Orange'
        : overallStatus === STATUS.WARNING
          ? 'Yellow'
          : 'Green';

  let alarmValue: SystemHealthAggregate['alarms'] = 'NONE';
  if (alarmMessages.length > 0) {
    if (overallStatus === STATUS.OFFLINE) alarmValue = 'CRITICAL';
    else if (overallStatus === STATUS.DEGRADED) alarmValue = 'EQUIPMENT FAULT';
    else if (overallStatus === STATUS.WARNING) alarmValue = 'WARNING';
  }

  const networkValue: SystemHealthAggregate['network'] =
    networkStatus === STATUS.OFFLINE
      ? 'DISCONNECTED'
      : networkStatus === STATUS.DEGRADED
        ? 'LIMITED'
        : networkStatus === STATUS.WARNING
          ? 'INTERMITTENT'
          : 'CONNECTED';

  return {
    overallStatus: overallWord,
    overallColour: STATUS_COLOUR[overallStatus],
    power: powerStatus === STATUS.OPERATIONAL ? 'NORMAL' : 'ABNORMAL',
    powerColour: STATUS_COLOUR[powerStatus],
    network: networkValue,
    networkColour: STATUS_COLOUR[networkStatus],
    alarms: alarmValue,
    alarmsColour: alarmMessages.length > 0 ? STATUS_COLOUR[overallStatus] : 'green',
    alarmDetails: alarmMessages,
    componentCount: componentStatuses.length,
    degradedCount: componentStatuses.filter(
      (c) => c.status === STATUS.DEGRADED || c.status === STATUS.OFFLINE,
    ).length,
    warningCount: componentStatuses.filter((c) => c.status === STATUS.WARNING).length,
  };
}

/** Components that depend, directly or through a cascade, on the given one (in dependency-table order). */
export function getAffectedComponents(componentId: string): string[] {
  const affected: string[] = [];
  const checked: Record<string, boolean> = {};
  const findAffected = (id: string): void => {
    if (checked[id]) return;
    checked[id] = true;
    for (const key of Object.keys(COMPONENT_DEPENDENCIES)) {
      if (COMPONENT_DEPENDENCIES[key].indexOf(id) !== -1) {
        affected.push(key);
        findAffected(key);
      }
    }
  };
  findAffected(componentId);
  return affected;
}

/** Inside the range OPERATIONAL, within a 20 % margin WARNING, within 40 % DEGRADED, beyond OFFLINE. */
export function deriveFromNormalRange(
  value: number,
  normalRange: NormalRangeLike | null | undefined,
): StatusLevel {
  if (!normalRange) return STATUS.OPERATIONAL;
  const min = (normalRange.low !== undefined ? normalRange.low : normalRange.min) as number;
  const max = (normalRange.high !== undefined ? normalRange.high : normalRange.max) as number;
  const margin = (max - min) * 0.2;
  if (value >= min && value <= max) return STATUS.OPERATIONAL;
  if (value >= min - margin && value <= max + margin) return STATUS.WARNING;
  if (value >= min - margin * 2 && value <= max + margin * 2) return STATUS.DEGRADED;
  return STATUS.OFFLINE;
}

// ------------------------------------------------- readings and whole elements

/** Store history buffers (oldest first) as the newest-first points the rules read. */
export function historyPointsFromReadings(buffer: Reading[]): HistoryPoint[] {
  const points: HistoryPoint[] = [];
  for (let i = buffer.length - 1; i >= 0; i--) {
    points.push({ datetime: new Date(buffer[i].timeMs).toISOString(), value: buffer[i].value });
  }
  return points;
}

/**
 * Deep copy of the subsystem tree with the live readings applied: a reading
 * replaces the item's value and status colour; a history buffer for the key
 * replaces the shipped history, otherwise the shipped history stays.
 */
export function withReadings(
  subsystems: Subsystem[],
  readings: Record<string, Reading> = {},
  history: Record<string, Reading[]> = {},
): Subsystem[] {
  return subsystems.map((s) => ({
    ...s,
    data: (s.data || []).map((item): DataItem => {
      const key = readingKey(s.id, item.id);
      const reading = readings[key];
      const buffer = history[key];
      const copy: DataItem = { ...item };
      if (reading) {
        copy.value = reading.value;
        copy.status = reading.status;
      }
      if (buffer && buffer.length > 0) copy.history = historyPointsFromReadings(buffer);
      return copy;
    }),
    childs: s.childs ? withReadings(s.childs, readings, history) : s.childs,
  }));
}

function colourToLevel(colour: string | undefined): StatusLevel | null {
  if (colour === 'green') return STATUS.OPERATIONAL;
  if (colour === 'yellow') return STATUS.WARNING;
  if (colour === 'red') return STATUS.OFFLINE;
  return null;
}

/** Red and yellow descendants the way the old cards counted them (see deriveAllStatuses). */
export function countBadges(subsystem: Subsystem): { issues: number; warnings: number } {
  let issues = 0;
  let warnings = 0;
  const countItems = (s: Subsystem, isDescendant: boolean): void => {
    for (const d of s.data || []) {
      if (d.status === 'red') issues++;
      else if (d.status === 'yellow') warnings++;
    }
    if (isDescendant) {
      const overall = (s.data || []).find((d) => d.id === 'overall_status');
      const title = overall ? String(overall.value).toLowerCase() : '';
      if (title === 'red') issues++;
      else if (title === 'yellow') warnings++;
    }
    for (const c of s.childs || []) countItems(c, true);
  };
  countItems(subsystem, false);
  return { issues, warnings };
}

/** The aggregate's colour word as a level. */
export function levelFromAggregate(word: SystemHealthAggregate['overallStatus']): StatusLevel {
  return word === 'Red'
    ? STATUS.OFFLINE
    : word === 'Orange'
      ? STATUS.DEGRADED
      : word === 'Yellow'
        ? STATUS.WARNING
        : STATUS.OPERATIONAL;
}

/**
 * Status of every subsystem under the current readings.
 *
 * The readings are applied to a copy of the element; nothing is written back
 * into the data items (the shipped values such as "Locked" and "Signal"
 * stay, and the derived level lives here only). Per subsystem:
 *   - own level: for system_health the aggregate of every ruled component
 *     (aggregateSystemHealth); otherwise the derivation's level when it
 *     evaluated a sensor or the rule derives from children; otherwise the
 *     colour of its status or overall_status item (green OPERATIONAL, yellow
 *     WARNING, red OFFLINE); otherwise OPERATIONAL;
 *   - level: the own level raised to the worst of (a) its own readings, an
 *     item's status colour read the same way (a red reading makes the card
 *     at least OFFLINE even when the rule's thresholds put the value lower,
 *     the live bridge colouring a value outside the sensor's own limits),
 *     and (b) its children's levels, so a card is never less severe than
 *     anything inside it. system_health is raised to the worst of the
 *     components it depends on (COMPONENT_DEPENDENCIES.system_health), its
 *     children in the dependency table;
 *   - colour: statusToColour(level);
 *   - issues and warnings: the red and yellow data items of the subsystem
 *     and all its descendants, plus any descendant card whose overall_status
 *     value is literally "red" or "yellow" (the old card title carried that
 *     value as its data-status and the badge counted every match).
 */
export function deriveAllStatuses(
  element: Element,
  readings: Record<string, Reading> = {},
  history: Record<string, Reading[]> = {},
): Record<string, SubsystemStatus> {
  // RFIMS measurements do not establish operational health or validated limits.
  if (isRfims(element.id)) return {};
  const tree = withReadings(element.subsystems, readings, history);
  const aggregate = aggregateSystemHealth({ subsystems: tree });
  const result: Record<string, SubsystemStatus> = {};
  const worse = (a: StatusLevel, b: StatusLevel): StatusLevel =>
    getStatusPriority(b) > getStatusPriority(a) ? b : a;
  // Children first, so a parent's level can include theirs.
  const visit = (s: Subsystem): StatusLevel => {
    let childWorst: StatusLevel = STATUS.OPERATIONAL;
    for (const c of s.childs || []) childWorst = worse(childWorst, visit(c));
    const rule = COMPONENT_RULES[s.id];
    const derivation = deriveComponentStatus(s);
    let level: StatusLevel;
    if (s.id === 'system_health') {
      level = levelFromAggregate(aggregate.overallStatus);
    } else if (derivation.sensorStatuses.length > 0 || (rule && rule.derivesFromChildren)) {
      level = derivation.status;
    } else {
      const statusItem = (s.data || []).find((d) => d.id === 'status' || d.id === 'overall_status');
      level = colourToLevel(statusItem?.status) ?? STATUS.OPERATIONAL;
    }
    for (const d of s.data || []) level = worse(level, colourToLevel(d.status) ?? STATUS.OPERATIONAL);
    level = worse(level, childWorst);
    const { issues, warnings } = countBadges(s);
    result[s.id] = { colour: statusToColour(level), level, issues, warnings };
    return level;
  };
  for (const s of tree) visit(s);
  const health = result.system_health;
  if (health) {
    let level = health.level;
    for (const id of COMPONENT_DEPENDENCIES.system_health ?? [])
      if (result[id]) level = worse(level, result[id].level);
    result.system_health = { ...health, level, colour: statusToColour(level) };
  }
  return result;
}
